<template>
  <div class="auth-wrapper auth-v1">
    <div class="auth-inner">
      <v-card class="auth-card">
        <!-- logo -->
        <v-card-title class="d-flex align-center justify-center py-7">
          <router-link to="/" class="d-flex align-center">
            <v-img :src="require(`@/assets/images/logos/logo.png`)" max-width="200px" alt="logo" contain class="me-0">
            </v-img>
          </router-link>
        </v-card-title>

        <v-card-text>
          <p class="mb-2" style="color: red">{{ this.snackBarMessage }}</p>
        </v-card-text>

        <!-- title -->
        <v-card-text>
          <p class="text-2x4 font-weight-semibold text--primary mb-2">
            Welcome to Our Top Up Community! 👋🏻
          </p>
          <p class="mb-2">
            Please sign-in to your account and start the adventure
          </p>
        </v-card-text>

        <!-- login form -->
        <v-card-text>
          <v-form @submit.prevent="logIn">
            <v-text-field v-model="username" outlined label="Username" ref="username.value" placeholder="JohnDoe"
              hide-details class="mb-3" :disabled="checking">
            </v-text-field>

            <v-text-field v-model="password" outlined :type="isPasswordVisible ? 'text' : 'password'" label="Password"
              ref="password.value" placeholder="············" :append-icon="
                isPasswordVisible ? icons.mdiEyeOffOutline : icons.mdiEyeOutline
              " hide-details @click:append="isPasswordVisible = !isPasswordVisible"
              :disabled="checking">
            </v-text-field>

            <div class="d-flex align-center justify-space-between flex-wrap">
              <v-checkbox label="Remember Me" hide-details class="me-3 mt-1" :disabled="checking">
              </v-checkbox>

              <!-- forgot link -->
              <a href="/request_new_password" class="mt-1"> Forgot Password? </a>
            </div>

            <!-- Affichage du spinner pendant vérification ou chargement -->
            <spinner v-if="checking || isLoading"></spinner>
            <v-btn v-else block color="primary" class="mt-6" type="submit" :disabled="checking">
              Login
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </div>

    <!-- background triangle shape  -->
    <img class="auth-mask-bg" height="173" :src="
      require(`@/assets/images/misc/mask-${
        $vuetify.theme.dark ? 'dark' : 'light'
      }.png`)
    " />

    <!-- tree -->
    <v-img class="auth-tree" width="247" height="185" src="@/assets/images/misc/tree.png"></v-img>

    <!-- tree  -->
    <v-img class="auth-tree-3" width="377" height="289" src="@/assets/images/misc/tree-3.png"></v-img>
  </div>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import axios from 'axios'
import store from "../../store";
import { useRouter } from '@/utils'
import { mdiEyeOutline, mdiEyeOffOutline } from "@mdi/js";
import { ref, computed } from "@vue/composition-api";
import Spinner from "@/components/Spinner.vue";

export default {
  setup() {
    store.commit("initialiseStore");

    const isPasswordVisible = ref(false);
    const snackBarMessage = computed(() => store.state.snackBarMessage);
    const isLoading = computed(() => store.state.isLoading);
    const username = ref("");
    const password = ref("");

    // Nouvel état pour la vérification préalable
    const checking = ref(false);

    return {
      isPasswordVisible,
      isLoading,
      snackBarMessage,
      username,
      password,
      checking,    // on expose checking
      icons: {
        mdiEyeOutline,
        mdiEyeOffOutline,
      },
    };
  },

  mounted() {
    document.title = "PettyCash: TopUp | Log In"
    if (this.$store.state.isAuthenticated) {
      this.$store.commit('setSnackBarMessage', "You are already logged in.")
      this.$store.commit('setSnackBarColor', "red darken-4")
      this.$store.commit('activateSnackBar', true)
      this.$router.push({ name: 'savings.dashboard' })
    }
  },

  methods: {
    async logIn() {
      // On évite les doubles soumissions
      if (this.checking || this.isLoading) return;

      axios.defaults.headers.common['Authorization'] = ""
      localStorage.removeItem('token')

      if (this.username.length < 4) {
        this.$store.commit('setSnackBarMessage', "Username is too short (min 4 characters)")
        this.$store.commit('setSnackBarColor', "red darken-4")
        this.$store.commit('activateSnackBar', true)
        this.$refs.username.focus()
        return
      }
      if (this.password.length < 8) {
        this.$store.commit('setSnackBarMessage', "Password is too short (min 8 characters)")
        this.$store.commit('setSnackBarColor', "red darken-4")
        this.$store.commit('activateSnackBar', true)
        this.$refs.password.focus()
        return
      }

      // ═══════════════════════════════════════════
      // 🔍 Étape 1 : Vérification blocage (obligatoire)
      // ═══════════════════════════════════════════
      this.checking = true;   // active le spinner
      try {
        const checkRes = await axios.post('/api/v1/topup/topup-blocked/', {
          username: this.username
        });

        if (checkRes.data.is_blocked) {
          this.$store.commit('setSnackBarMessage', "Your account has been temporarily suspended. Reach out to support if you think this is a mistake.")
          this.$store.commit('setSnackBarColor', "warning")
          this.$store.commit('activateSnackBar', true)
          this.checking = false;
          return; // 🚫 Bloque la connexion
        }
      } catch (err) {
        // Si l'appel échoue (réseau, 500, etc.), on refuse le login pour éviter de contourner la vérification
        console.error('Check blocked endpoint error:', err);
        this.$store.commit('setSnackBarMessage', "Unable to verify account status. Please try again later.")
        this.$store.commit('setSnackBarColor', "red darken-4")
        this.$store.commit('activateSnackBar', true)
        this.checking = false;
        return;
      }

      // ✅ Vérification passée, on peut tenter la connexion
      this.checking = false;
      const formData = {
        username: this.username,
        password: this.password
      };

      try {
        const response = await axios.post('/api/v1/token/login/', formData);
        const token = response.data.auth_token;
        this.$store.commit('setToken', token);
        axios.defaults.headers.common['Authorization'] = "Token " + token;
        localStorage.setItem('token', token);
        this.$store.state.username = this.username;

        this.$router.push({ name: 'savings.dashboard' });
        this.$store.commit('setSnackBarMessage', "Welcome back " + this.username + " 😃");
        this.$store.commit('setSnackBarColor', "teal darken-4");
        this.$store.commit('activateSnackBar', true);
      } catch (error) {
        if (error.response) {
          for (const property in error.response.data) {
            this.$store.commit('setSnackBarMessage', error.response.data[property][0]);
            this.$store.commit('setSnackBarColor', "red darken-4");
            this.$store.commit('activateSnackBar', true);
          }
        } else {
          this.$store.commit('setSnackBarMessage', "An error occurred while logging in.");
          this.$store.commit('setSnackBarColor', "red darken-4");
          this.$store.commit('activateSnackBar', true);
        }
      }
    }
  },

  components: { Spinner }
};
</script>

<style lang="scss">
@import "~@/plugins/vuetify/default-preset/preset/pages/auth.scss";
</style>
